import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { plr } from './tokens';
import { alertCircleIcon, clockIcon } from './icons';

const slide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

export function IndeterminateBar({ label }: { label: string }) {
  return (
    <Box
      role="progressbar"
      aria-label={label}
      sx={{
        height: 6, borderRadius: '3px', background: plr.stepTodo, overflow: 'hidden',
      }}
    >
      <Box sx={{
        width: '40%',
        height: 6,
        borderRadius: '3px',
        background: plr.ink,
        animation: `${slide} 1.6s ease-in-out infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
      />
    </Box>
  );
}

// Mensajes que rotan por tiempo (versión 3f sin estado por fuente: hoy el
// backend responde todo al final, así que no mostramos checks por fuente).
export function useRotatingMessage(messages: string[], everyMs = 6000) {
  const [index, setIndex] = React.useState(0);
  React.useEffect(() => {
    if (messages.length < 2) return undefined;
    const id = setInterval(() => {
      setIndex((i) => Math.min(i + 1, messages.length - 1));
    }, everyMs);
    return () => clearInterval(id);
  }, [messages.length, everyMs]);
  return messages[index];
}

type PlrWaitingProps = {
  messages: string[],
  detail?: string,
  note?: string,
  error?: string,
  testId?: string,
};

// Bloque de espera (3f y comprobación de RUT): barra indeterminada + mensaje.
export default function PlrWaiting(props: PlrWaitingProps) {
  const {
    messages, detail, note, error, testId,
  } = props;
  const message = useRotatingMessage(messages);
  return (
    <>
      {error ? (
        <Box
          role="alert"
          sx={{
            mt: '10px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            background: plr.surface,
            borderRadius: '16px',
            p: '14px 16px',
            fontSize: { xs: 14, sm: 15 },
            lineHeight: 1.45,
            color: plr.body,
          }}
        >
          <Box component="span" sx={{ color: plr.danger, mt: '1px', display: 'flex' }}>
            {alertCircleIcon({ size: 18, strokeWidth: 2.2 })}
          </Box>
          <span>{error}</span>
        </Box>
      ) : (
        <Box
          data-pw={testId}
          sx={{
            mt: '18px', display: 'flex', flexDirection: 'column', gap: '14px',
          }}
        >
          <IndeterminateBar label={message} />
          <Box
            component="p"
            aria-live="polite"
            sx={{
              m: 0, fontSize: { xs: 16, sm: 17 }, fontWeight: 600, color: plr.ink,
            }}
          >
            {message}
          </Box>
          {detail && (
            <Box
              component="p"
              sx={{
                m: '-6px 0 0', fontSize: { xs: 13.5, sm: 14.5 }, lineHeight: 1.5, color: plr.muted,
              }}
            >
              {detail}
            </Box>
          )}
        </Box>
      )}
      {note && !error && (
        <Box sx={{
          mt: { xs: 'auto', sm: '24px' },
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          background: plr.surface,
          borderRadius: '16px',
          p: '12px 14px',
        }}
        >
          <Box component="span" sx={{ color: plr.accent, mt: '1px', display: 'flex' }}>
            {clockIcon({ size: 18 })}
          </Box>
          <Box component="span" sx={{ fontSize: 13, lineHeight: 1.45, color: plr.body }}>
            {note}
          </Box>
        </Box>
      )}
    </>
  );
}

PlrWaiting.defaultProps = {
  detail: undefined,
  note: undefined,
  error: undefined,
  testId: undefined,
};
